
import { sizeFormatter } from '@girder/components/src/utils/mixins';
import {
  defineComponent, ref, computed, watch, Ref,
} from '@vue/composition-api';
import store from '../store';
import {
  LOAD_DATASET,
  SET_DATASET_NAME, SET_DATASET_DESCRIPTION, SET_DATASET_GROUP_LEVELS, REMERGE_DATASET,
} from '../store/actions.type';
import { INTIALIZE_DATASET } from '../store/mutations.type';

import MergeMethods from './MergeMethods.vue';

interface Dataset {
  id: string;
  created: Date;
  name: string;
  description: string;
  width: number;
  height: number;
  size: number;
  meta: {
    merge_method: string;
    merged: [];
  };
  groupLevels: GroupLevel[];
}

interface GroupLevel {
  color: string;
  description: string;
  label: string;
  name: string;
}

export default defineComponent({
  props: {
    id: {
      type: String,
      required: true,
      default: null,
    },
  },
  components: {
    MergeMethods,
  },
  setup(props) {
    // Data
    const valid = ref(false);
    const requiredRules = ref([
      (v: string) => !!v.trim() || 'Name is required',
    ]);
    const groupLevelHeaders = ref([
      {
        text: 'Name',
        value: 'name',
        sortable: true,
      },
      {
        text: 'Label',
        value: 'label',
        sortable: true,
      },
      {
        text: 'Description',
        value: 'description',
        sortable: false,
      },
      {
        text: 'Color',
        value: 'color',
        sortable: false,
      },
    ]);
    const method = ref(null) as Ref<string | null>;

    // Computed
    const dataset = computed((): Dataset => store.getters.dataset(props.id));
    const ready = computed((): boolean => store.getters.ready(props.id));
    const groupLevels = computed((): GroupLevel[] => dataset.value.groupLevels);
    const isMerged = computed((): boolean => store.getters.isMerged(props.id));
    const mergedDatasets = computed(() => {
      if (!isMerged.value) {
        return [];
      }
      const merged = dataset.value.meta.merged || [];
      return merged.map((d) => {
        const ds = store.getters.dataset(d);
        if (!ds) {
          return {
            id: d,
            name: 'Deleted Data Source',
            valid: false,
            description: '',
          };
        }
        return {
          id: ds.id,
          name: ds.name,
          description: ds.description,
          valid: store.getters.valid(ds.id),
        };
      });
    });
    // eslint-disable-next-line max-len
    const allDatasetsValid = computed(() => isMerged.value && mergedDatasets.value.every((d) => d.valid));

    // Methods
    const { formatSize } = sizeFormatter.methods;
    function setName(name: string) {
      if (!name.trim()) {
        return;
      }
      store.dispatch(SET_DATASET_NAME, { dataset_id: props.id, name: name.trim() });
    }
    function setDescription(description: string) {
      store.dispatch(SET_DATASET_DESCRIPTION, { dataset_id: props.id, description });
    }
    function changeGroupLevel(
      groupLevel: GroupLevel,
      change: {
        label?: string;
        description?: string;
        color?: string;
      },
    ) {
      const levels = groupLevels.value.map((level) => {
        if (level === groupLevel) {
          return { ...level, ...change };
        }
        return level;
      });
      store.dispatch(SET_DATASET_GROUP_LEVELS, { dataset_id: props.id, groupLevels: levels });
    }
    function remerge() {
      if (!allDatasetsValid.value) {
        return;
      }
      store.dispatch(REMERGE_DATASET, { dataset_id: props.id, method: method.value });
    }

    watch(dataset, (newValue) => {
      if (newValue && store.getters.isMerged(newValue.id)) {
        method.value = newValue.meta.merge_method;
      } else {
        method.value = null;
      }
    });

    if (!dataset.value) {
      // initialize the dataset to prevent NPE race conditions during slow loads
      store.commit(INTIALIZE_DATASET, { dataset_id: props.id });
      store.dispatch(LOAD_DATASET, { dataset_id: props.id });
    }
    if (dataset.value && store.getters.isMerged(dataset.value.id)) {
      method.value = dataset.value.meta.merge_method;
    }

    return {
      valid,
      requiredRules,
      groupLevelHeaders,
      method,
      dataset,
      ready,
      groupLevels,
      isMerged,
      mergedDatasets,
      allDatasetsValid,
      formatSize,
      setName,
      setDescription,
      changeGroupLevel,
      remerge,
    };
  },
});
